// Pointer dragging avoids the editor's capture-phase file-drop handlers and
// supports mouse, pen and touch using the same insertion-position calculation.
const initAnnotationReorder = (
    list: HTMLElement,
    getRows: () => HTMLElement[],
    move: (from: number, to: number) => void
) => {
    let cancel = () => {};
    list.addEventListener('pointerdown', (start: PointerEvent) => {
        const handle = (start.target as HTMLElement).closest<HTMLElement>('.annotation-list-drag-handle');
        if (!handle || !start.isPrimary || start.button !== 0) return;
        cancel();
        start.preventDefault();
        start.stopPropagation();

        const rows = getRows();
        const source = handle.closest<HTMLElement>('.annotation-list-row');
        if (!source) return;
        const from = rows.indexOf(source);
        if (from < 0) return;
        const bounds = source.getBoundingClientRect();
        let x = start.clientX;
        let y = start.clientY;
        let to = from;
        let valid = false;
        let preview: HTMLElement;
        let frame = 0;
        let lastTime = 0;
        const marker = document.createElement('div');
        marker.className = 'annotation-list-drop-marker';
        marker.setAttribute('aria-hidden', 'true');
        const controller = new AbortController();
        const options = { signal: controller.signal };

        const update = () => {
            preview.style.left = `${x - (start.clientX - bounds.left)}px`;
            preview.style.top = `${y - (start.clientY - bounds.top)}px`;
            const rect = list.getBoundingClientRect();
            valid = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
            marker.hidden = !valid;
            if (!valid) return;
            const others = rows.filter(row => row !== source);
            to = others.findIndex((row) => {
                const box = row.getBoundingClientRect();
                return y < box.top + box.height / 2;
            });
            if (to < 0) to = others.length;
            const next = others[to];
            const previous = others[to - 1];
            const edge = next?.getBoundingClientRect().top ?? previous?.getBoundingClientRect().bottom ?? bounds.top;
            marker.style.left = `${bounds.left}px`;
            marker.style.top = `${Math.max(rect.top, Math.min(rect.bottom - 2, edge))}px`;
            marker.style.width = `${bounds.width}px`;
        };
        const scroll = (time: number) => {
            const elapsed = lastTime ? Math.min(time - lastTime, 32) : 16;
            lastTime = time;
            const rect = list.getBoundingClientRect();
            if (valid) {
                const edge = Math.min(32, rect.height / 3);
                const speed = y < rect.top + edge ? -Math.min(1, (rect.top + edge - y) / edge) :
                    y > rect.bottom - edge ? Math.min(1, (y - rect.bottom + edge) / edge) : 0;
                list.scrollTop += speed * elapsed * 0.4;
            }
            update();
            frame = requestAnimationFrame(scroll);
        };
        cancel = () => {
            controller.abort();
            cancelAnimationFrame(frame);
            preview?.remove();
            marker.remove();
            source.classList.remove('dragging');
            if (handle.hasPointerCapture(start.pointerId)) handle.releasePointerCapture(start.pointerId);
            cancel = () => {};
        };
        handle.setPointerCapture(start.pointerId);
        handle.addEventListener('pointermove', (event: PointerEvent) => {
            if (event.pointerId !== start.pointerId) return;
            event.preventDefault();
            event.stopPropagation();
            x = event.clientX;
            y = event.clientY;
            if (!preview) {
                if (Math.hypot(x - start.clientX, y - start.clientY) < 4) return;
                preview = source.cloneNode(true) as HTMLElement;
                preview.classList.add('annotation-list-drag-preview');
                preview.setAttribute('aria-hidden', 'true');
                preview.inert = true;
                preview.style.width = `${bounds.width}px`;
                document.body.append(preview, marker);
                source.classList.add('dragging');
                frame = requestAnimationFrame(scroll);
            }
            update();
        }, options);
        handle.addEventListener('pointerup', (event: PointerEvent) => {
            if (event.pointerId !== start.pointerId) return;
            event.preventDefault();
            event.stopPropagation();
            if (preview) {
                x = event.clientX;
                y = event.clientY;
                update();
            }
            const commit = preview && valid && to !== from;
            cancel();
            if (commit) move(from, to);
        }, options);
        handle.addEventListener('pointercancel', cancel, options);
        handle.addEventListener('lostpointercapture', cancel, options);
        window.addEventListener('blur', cancel, options);
        window.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopImmediatePropagation();
                cancel();
            }
        }, { ...options, capture: true });
    });
    return () => cancel();
};

export { initAnnotationReorder };
